"use client";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { getResizedImageUrl } from "../../utils/getResizedImageUrl";
import { globalImageCache, isImageLoaded, isImageLoading } from "../../utils/imageCache";
import { getImagePriority, isAboveFold } from "@/utils/imagePriority";
import { useInViewWithPriority } from "./useInView";
import styles from "./TokenIcon.module.scss";

interface TokenIconProps {
  src: string;
  alt: string;
  verified?: boolean;
  className?: string;
  preload?: boolean;
  width?: number;
  index?: number; // Token index for priority calculation
  isSwiper?: boolean; // Whether this is a swiper token
  isHero?: boolean; // Whether this is a hero image
}

const MISSING_ICON = '/tkn_lnd_pg_assets/images/missing_token_icon.svg';

// In-memory cache for resolved image URLs (for URL processing, not image loading)
const resolvedImageCache = new Map<string, string>();

// Helper to check if image is external
const isExternalImage = (url: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin !== window.location.origin;
  } catch {
    return false;
  }
};

// Helper to check if image needs crossOrigin attribute
const needsCrossOrigin = (url: string): boolean => {
  // Only add crossOrigin for images that are likely to need it
  // and are from domains that support CORS
  if (typeof window === 'undefined') return false;
  
  try {
    const urlObj = new URL(url, window.location.origin);
    // Only add crossOrigin for specific domains that we know support it
    const corsSupportedDomains = [
      'media.solana-cdn.com',
      'solflare.com',
      'localhost',
      '127.0.0.1'
    ];
    return corsSupportedDomains.some(domain => urlObj.hostname.includes(domain));
  } catch {
    return false;
  }
};

const TokenIconComponent: React.FC<TokenIconProps> = ({
  src,
  alt,
  verified = true,
  className = '',
  // preload = true,
  width = 32,
  index = 0,
  isSwiper = false,
  isHero = false,
}) => {
  const isDefaultMissingIcon = !src || src.trim() === '' || src === MISSING_ICON || src.includes('missing_token_icon');
  const [imgSrc, setImgSrc] = useState(isDefaultMissingIcon ? MISSING_ICON : src);
  const [showSkeleton, setShowSkeleton] = useState(!isDefaultMissingIcon);
  const [isImageReady, setIsImageReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const loadStartTime = useRef<number>(0);
  const [windowWidth, setWindowWidth] = useState<number>(1024);

  // Calculate priority based on position and context
  const priority = getImagePriority({ 
    index, 
    isSwiper, 
    isHero, 
    isAboveFold: isAboveFold(index, windowWidth),
    windowWidth 
  });

  // Use intersection observer for priority-based loading
  const { ref, shouldLoad } = useInViewWithPriority(priority);

  // Simplified loading strategy based on index
  const simplifiedLoading = index < 5 ? 'eager' : 'lazy';

  
  // Track window width for responsive priority
  useEffect(() => {
    const updateWindowWidth = () => {
      setWindowWidth(window.innerWidth);
    };

    updateWindowWidth();
    window.addEventListener('resize', updateWindowWidth);
    return () => window.removeEventListener('resize', updateWindowWidth);
  }, []);

  useEffect(() => {
    if (isDefaultMissingIcon) {
      setImgSrc(MISSING_ICON);
      setShowSkeleton(false);
      setIsImageReady(true);
      setHasError(false);
      return;
    }
    
    // For high priority images, load immediately
    // For medium/low priority images, only load when shouldLoad is true
    if (priority !== 'high' && !shouldLoad) {
      return;
    }
    
    loadStartTime.current = performance.now();
    setShowSkeleton(true);
    setIsImageReady(false);
    setHasError(false);
    
    // Check if URL is already processed (contains media.solana-cdn.com)
    const isAlreadyProcessed = src.includes('media.solana-cdn.com');
    
    if (isAlreadyProcessed) {
      // URL is already processed server-side, use directly
      setImgSrc(src);
      setShowSkeleton(false);
      
      // For external images, don't rely on preloading to avoid CORS issues
      if (isExternalImage(src)) {
        setIsImageReady(true);
      } else {
        // For local images, we can use the cache
        if (isImageLoaded(src)) {
          setIsImageReady(true);
        } else if (isImageLoading(src)) {
          const checkLoaded = () => {
            if (isImageLoaded(src)) {
              setIsImageReady(true);
            } else if (isImageLoading(src)) {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
        } else {
          globalImageCache.preload(src).then(() => {
            setIsImageReady(true);
          }).catch(() => {
            setIsImageReady(true);
          });
        }
      }
      return;
    }
    
    // Fallback: process URL client-side if not already processed
    const cacheKey = `${src}|w=${width}`;
    if (resolvedImageCache.has(cacheKey)) {
      const processedUrl = resolvedImageCache.get(cacheKey) || MISSING_ICON;
      setImgSrc(processedUrl);
      setShowSkeleton(false);
      
      // For external images, don't rely on preloading
      if (isExternalImage(processedUrl)) {
        setIsImageReady(true);
      } else {
        // For local images, use cache
        if (isImageLoaded(processedUrl)) {
          setIsImageReady(true);
        } else if (isImageLoading(processedUrl)) {
          const checkLoaded = () => {
            if (isImageLoaded(processedUrl)) {
              setIsImageReady(true);
            } else if (isImageLoading(processedUrl)) {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
        } else {
          globalImageCache.preload(processedUrl).then(() => {
            setIsImageReady(true);
          }).catch(() => {
            setIsImageReady(true);
          });
        }
      }
      return;
    }
    
    // Only resize if not data: or local asset
    if (src.startsWith('data:') || src.startsWith('/')) {
      setImgSrc(src);
      setShowSkeleton(false);
      resolvedImageCache.set(cacheKey, src);
      setIsImageReady(true);
      return;
    }
    
    const resolvedUrl = getResizedImageUrl(src, { width, cache: '31536000' });
    setImgSrc(resolvedUrl);
    setShowSkeleton(false);
    resolvedImageCache.set(cacheKey, resolvedUrl);
    
    // For external images, don't preload to avoid CORS issues
    if (isExternalImage(resolvedUrl)) {
      setIsImageReady(true);
    } else {
      globalImageCache.preload(resolvedUrl).then(() => {
        setIsImageReady(true);
      }).catch(() => {
        setIsImageReady(true);
      });
    }
  }, [src, isDefaultMissingIcon, width, shouldLoad]);

  const handleError = () => {
    setHasError(true);
    if (imgSrc !== MISSING_ICON) {
      setImgSrc(MISSING_ICON);
    }
  };

  const handleLoad = () => {
    if (loadStartTime.current > 0) {
      loadStartTime.current = 0;
    }
    setIsImageReady(true);
    setHasError(false);
  };

  // Show skeleton while image is not ready
  if (showSkeleton && !isImageReady) {
    return (
      <span
        className={`${styles.tokenIcon} ${styles.skeleton} ${className}`}
        style={{
          display: 'inline-block',
          width,
          height: width,
          borderRadius: '50%',
          background: '#23242a',
        }}
        aria-label={alt}
        ref={ref}
      />
    );
  }

  // Get loading strategy based on priority
  // const loadingStrategy = getLoadingStrategy(priority);
  // const preloadStrategy = getPreloadStrategy(priority);
  
  // Use simplified loading for better performance (index-based)
  const finalLoadingStrategy = simplifiedLoading;

  // Use next/image for Solana CDN or local assets, <img> for others
  const isSolanaCdn = imgSrc.startsWith('https://media.solana-cdn.com/');
  const isLocal = imgSrc.startsWith('/') && !imgSrc.startsWith('http');

  const iconImg = (isSolanaCdn || isLocal) ? (
    <Image
      src={imgSrc}
      alt={alt}
      className={`${styles.tokenIcon} ${className}`}
      width={width}
      height={width}
      onError={handleError}
      onLoad={handleLoad}
      style={{ 
        opacity: isImageReady ? 1 : 0.5, 
        transition: 'opacity 0.2s ease-in-out' 
      }}
      loading={finalLoadingStrategy}
      priority={index < 5}
      unoptimized={isLocal || isSolanaCdn}
      ref={ref}
    />
  ) : (
    <img
      src={imgSrc}
      alt={alt}
      className={`${styles.tokenIcon} ${className}`}
      width={width}
      height={width}
      onError={handleError}
      onLoad={handleLoad}
      style={{ 
        opacity: isImageReady ? 1 : 0.5, 
        transition: 'opacity 0.2s ease-in-out' 
      }}
      loading={finalLoadingStrategy}
      {...(needsCrossOrigin(imgSrc) ? { crossOrigin: 'anonymous' } : {})}
      ref={ref}
    />
  );

  if (verified) return iconImg;

  return (
    <div className={styles.notVerifiedWrapper}>
      {iconImg}
      <Image
        src="/tkn_lnd_pg_assets/images/icons/icon-warning-square.svg"
        alt="not verified"
        className={styles.notVerifiedIcon}
        width={20}
        height={20}
        loading="lazy"
      />
    </div>
  );
};

// Export memoized component for performance optimization
export const TokenIcon = React.memo(TokenIconComponent);
